import axios, {
  AxiosError,
  AxiosResponse,
  Method,
  RawAxiosRequestHeaders,
} from "axios";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./useAuth";

interface Params {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Args<T> {
  url?: string;
  params?: Params;
  body?: Params;
  onStart?: () => void;
  onEnd?: () => void;
  onSuccess?: GenericFunction;
  onError?: (err: AxiosError) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface PostRequest<T> {
  method?: Method;
  url?: string;
  params?: Params;
  body?: Params;
  headers?: RawAxiosRequestHeaders;
  onStart?: () => void;
  onEnd?: () => void;
  onSuccess?: GenericFunction;
  onError?: (err: AxiosError) => void;
}

type Post<T> = ({ params, url }: Args<T>) => Promise<void>;
type GenericFunction = <T>(data?: T) => void;

interface PostResponse<T> {
  post: Post<T>;
  isLoading: boolean;
}

export function usePost<T>({
  method = "post",
  url,
  params,
  headers,
  onStart,
  onEnd,
  onSuccess,
  onError,
}: PostRequest<T>): PostResponse<T> {
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { authInfo } = useAuth();
  const methodInternal = useMemo(() => method, [method]);
  const urlInternal = useMemo(() => url, [url]);
  const paramsInternal = useMemo(() => params, [params]);
  const [isLoading, setLoading] = useState(false);
  const post = useCallback<Post<T>>(
    // eslint-disable-next-line no-shadow
    async <T>(args: Args<T>) => {
      const reqUrl = args.url ?? urlInternal ?? "";
      if (!reqUrl) {
        return;
      }
      if (!methodInternal) {
        return;
      }

      if (args.onStart) {
        args.onStart();
      } else onStart?.();

      try {
        setLoading(true);
        const authHeaders = authInfo?.accessToken
          ? { authorization: `Bearer ${authInfo.accessToken}` }
          : {};

        const res: AxiosResponse<T> = await axios.request({
          method: method ?? methodInternal,
          url: reqUrl,
          data: args.params ?? paramsInternal ?? undefined,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...authHeaders,
            ...headers,
          },
        });
        if (args.onSuccess) {
          args.onSuccess(res.data);
        } else onSuccess?.(res.data);
      } catch (err) {
        if (err.message !== "") {
          enqueueSnackbar(err.message, { variant: "error" });
        } else {
          enqueueSnackbar(t("general.service_failed"), { variant: "error" });
        }
        if (args.onError) {
          args.onError(err as AxiosError);
        } else onError?.(err as AxiosError);
      } finally {
        setLoading(false);
        if (args.onEnd) {
          args.onEnd();
        } else onEnd?.();
      }
    },
    [headers, methodInternal, paramsInternal, urlInternal, authInfo]
  );

  const clear = useCallback(() => {
    setLoading(false);
  }, []);

  useEffect(() => () => clear(), [clear]);

  return {
    post,
    isLoading,
  };
}
